import React from "react";
import Button from "src/components/common/Button";
import CheckBox from "src/components/common/CheckBox";
import styles from "./styles.module.scss";

// Per-provider secret field schema. Field keys must match what
// market-data-service reads from the provider-secrets store. Markit is an SFTP
// feed (no API key); the rest are API tokens/keys. FIS is kept here so a valid
// key can be dropped in and toggled on later without a redeploy.
export const PROVIDERS = [
    // KOSCOM (stocks) is retired - stocks come from FIS / EOD_HISTORICAL now, so it
    // is no longer a configurable provider. KOSCOM-ETF is a separate integration
    // (ETF details) and stays.
    {
        id: "koscom-etf",
        label: "KOSCOM ETF",
        fields: [
            { key: "apikey", label: "API Key" },
            { key: "institutionkey", label: "Institution Key" },
        ],
    },
    { id: "eodhistorical", label: "EOD Historical", fields: [{ key: "token", label: "API Token" }] },
    { id: "permid", label: "PermID (RIC)", fields: [{ key: "token", label: "Access Token" }] },
    {
        id: "fis",
        label: "FIS",
        fields: [
            { key: "marketUser", label: "Market User" },
            { key: "marketPass", label: "Market Password" },
            { key: "holidayUser", label: "Holiday User" },
            { key: "holidayPass", label: "Holiday Password" },
            { key: "holidayUsergroup", label: "Holiday Usergroup" },
        ],
    },
    {
        id: "markit",
        label: "S&P Global",
        fields: [
            { key: "sftpHost", label: "SFTP Host" },
            { key: "sftpPath", label: "SFTP Path" },
            { key: "sftpUser", label: "SFTP User" },
            { key: "sftpPass", label: "SFTP Password" },
        ],
    },
];

/**
 * Platform-global market-data provider config: per-provider enable toggle +
 * secret fields. Presentational only - all pending state lives in the parent
 * PlatformSettings page and is applied through the page's single confirm/discard
 * bar. A configured field shows `•••••`; clicking Replace reveals an input to
 * overwrite it. Values are never returned by the server.
 */
const MarketDataProviders = ({
    stored,
    enabledById,
    secretsById,
    replacingById,
    onToggleEnabled,
    onStartReplace,
    onCancelReplace,
    onSecretChange,
}) => {
    return (
        <div className={styles.section}>
            <div className={styles.sectionTitle}>Market Data providers</div>
            <div className={styles.configForm}>
                {PROVIDERS.map((prov) => {
                    const s = stored[prov.id] || {};
                    const configured = s.configuredFields || [];
                    return (
                        <div key={prov.id} className={styles.providerRow}>
                            <div className={styles.providerHeader}>
                                <span className={styles.configLabel}>{prov.label}</span>
                                <CheckBox
                                    name={prov.id + "-enabled"}
                                    selected={!!enabledById[prov.id]}
                                    onChange={() => onToggleEnabled(prov.id)}
                                    small
                                    styleName="black"
                                    dataTestId={"md-" + prov.id + "-enabled"}
                                >
                                    <span className={styles.configHint}>Enabled</span>
                                </CheckBox>
                            </div>
                            <div className={styles.providerFields}>
                                {prov.fields.map((f) => {
                                    const isConfigured = configured.includes(f.key);
                                    const isReplacing = !!(replacingById[prov.id] || {})[f.key];
                                    const showInput = isReplacing || !isConfigured;
                                    return (
                                        <div key={f.key} className={styles.secretField}>
                                            <span className={styles.secretLabel}>{f.label}</span>
                                            {showInput ? (
                                                <>
                                                    {/* API keys are not login passwords: type=text (not
                                                        password) so browser/extension password managers never
                                                        offer to fill or save them; autocomplete off + the ignore
                                                        hints keep 1Password/LastPass/Bitwarden out too. */}
                                                    <input
                                                        type="text"
                                                        className={styles.configInput}
                                                        placeholder={isConfigured ? "New value" : "Not set"}
                                                        value={(secretsById[prov.id] || {})[f.key] || ""}
                                                        onChange={(e) => onSecretChange(prov.id, f.key, e.target.value)}
                                                        data-testid={"md-" + prov.id + "-" + f.key}
                                                        autoComplete="off"
                                                        spellCheck={false}
                                                        data-lpignore="true"
                                                        data-1p-ignore="true"
                                                        data-bwignore="true"
                                                        data-form-type="other"
                                                    />
                                                    {isConfigured && (
                                                        <Button
                                                            value="Keep existing"
                                                            onClick={() => onCancelReplace(prov.id, f.key)}
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <span className={styles.maskedSecret}>•••••</span>
                                                    <Button
                                                        value="Replace"
                                                        onClick={() => onStartReplace(prov.id, f.key)}
                                                        dataTestId={"md-" + prov.id + "-" + f.key + "-replace"}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MarketDataProviders;
